import { NgZone } from '@angular/core';

// The browser's own speech recogniser (Chrome, Edge, Safari - Brave blocks
// it). Listens for one short phrase, e.g. a container state.

export function speechSupported(): boolean {
  return typeof window !== 'undefined' && !!((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition);
}

export interface Listening {
  // Resolves with what was said, or rejects with a message to show.
  result: Promise<string>;
  stop: () => void;
}

export function listenOnce(zone: NgZone, onInterim?: (text: string) => void): Listening {
  const Recognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
  if (!Recognition) {
    return { result: Promise.reject("This browser can't listen - use the state menu instead."), stop: () => {} };
  }
  const recognition = new Recognition();
  recognition.lang = 'en-GB';
  recognition.interimResults = !!onInterim;
  recognition.continuous = false;
  recognition.maxAlternatives = 3;

  const result = new Promise<string>((resolve, reject) => {
    let heard = '';
    recognition.onresult = (event: any) =>
      zone.run(() => {
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const r = event.results[i];
          if (r.isFinal) {
            // All alternatives, so a state in a less likely reading still counts.
            heard = Array.from(r as ArrayLike<any>).map((alt: any) => alt.transcript).join(' | ');
          } else onInterim?.(r[0].transcript);
        }
      });
    recognition.onerror = (event: any) =>
      zone.run(() =>
        reject(
          event.error === 'not-allowed' || event.error === 'service-not-allowed'
            ? 'Microphone access is blocked - allow it in the address bar.'
            : event.error === 'network'
              ? "This browser's speech service isn't reachable (Brave blocks it) - use the state menu, or Chrome / Edge."
              : event.error === 'no-speech'
                ? "Didn't hear anything - try again."
                : 'Listening stopped - try again.',
        ),
      );
    recognition.onend = () => zone.run(() => (heard ? resolve(heard) : reject("Didn't catch that - try again.")));
  });
  recognition.start();
  return { result, stop: () => recognition.stop() };
}
